import FlowSubjectRow from "../features/calculator/components/FlowSubjectRow";
import FlowChoice from "../features/calculator/components/FlowChoice";
import FlowLevelSelect from "../features/calculator/components/FlowLevelSelect";
import useFlowDefaults from "../features/calculator/hooks/useFlowDefaults";
/* src/components/GroupTwo.jsx */
import React, { useState, useEffect } from "react";
import { useCalculatorStoredValue } from "../contexts/CalculatorPersistenceContext";
import { useTZ } from "../contexts/TimezoneContext";
import { useGradeActions } from "../contexts/GradesContext";
import GroupOne from "./GroupOne";
import { getEntryForSubject } from "../utils/timezone";
import { getBoundaryResolutionOrder } from "../utils/boundaryCatalog";
import useBoundaryCatalog from "../hooks/useBoundaryCatalog";
import { getBoundarySubjectMeta } from "../data/subjectCatalog";
import { getPointsAboveBoundary } from "../features/subjects/utils/subjectScoring";

const MARK_FIELDS = [
  ["writing", "P1",     30, "25%"],
  ["reading", "P2",     40, "25%"],
  ["listening", "P2(L)", 25, "25%"],
  ["oral",    "IO",     30, "25%"],
];

const LEVELS = ["SL", "HL"];

const EMPTY_RAW = { writing: "", reading: "", listening: "", oral: "" };

function GroupTwo({
  groupId = "2",
  boundary = "m24",
  onBoundaryNotFound,
  headerExtras = null,
  afterTitleExtras = null,
}) {
  const { tz } = useTZ();
  const { updateGrade } = useGradeActions();
  const { dataByBoundary, catalogKeys, isLoading } = useBoundaryCatalog(boundary, {
    includeAllBoundaries: true,
  });

  const storagePath = `groups.${groupId}.groupTwo`;
  const [track, setTrack] = useCalculatorStoredValue(`${storagePath}.track`, "langB");
  const [level, setLvl] = useCalculatorStoredValue(`${storagePath}.level`, "");
  const [subject, setSubject] = useCalculatorStoredValue(`${storagePath}.subject`, "");
  const [raw, setRaw] = useCalculatorStoredValue(`${storagePath}.raw`, EMPTY_RAW);
  useFlowDefaults(storagePath, "groupTwo", !isLoading && track === "langB");
  const [boundaryNotFound, setBoundaryNotFound] = useState(false);

  const switchTrack = (val) => {
    setTrack(val);
    setLvl("");
    setSubject("");
    setRaw(EMPTY_RAW);
    updateGrade(groupId, null, null, null);
  };

  const boundaryOrder = getBoundaryResolutionOrder(boundary);
  const groupTwoKeys = catalogKeys.filter(
    (k) => getBoundarySubjectMeta(k)?.groupId === "2"
  );

  const langOptions = level
    ? groupTwoKeys
        .filter((k) => new RegExp(`^${level} .+? (B|ab initio)$`, "i").test(k))
        .sort((a, b) => a.localeCompare(b))
    : [];

  const subjectKeyB = track === "langB" ? subject : null;
  let entryLangB = null;
  let resolvedBoundary = null;

  if (subjectKeyB) {
    for (const cb of boundaryOrder) {
      const cd = dataByBoundary[cb];
      if (!cd) continue;
      const ce = getEntryForSubject(cd.timezones, subjectKeyB, tz).entry;
      if (ce) { entryLangB = ce; resolvedBoundary = cb; break; }
    }
  }
  const entryAvailableLangB = Boolean(entryLangB);

  useEffect(() => {
    if (track !== "langB" || isLoading || !subjectKeyB) {
      if (boundaryNotFound) setBoundaryNotFound(false);
      return;
    }
    if (!entryAvailableLangB) {
      setBoundaryNotFound((prev) => (prev ? prev : true));
    } else if (boundaryNotFound) {
      setBoundaryNotFound(false);
    }
  }, [track, isLoading, subjectKeyB, entryAvailableLangB, boundaryNotFound]);

  useEffect(() => { setBoundaryNotFound(false); }, [boundary]);

  const hasCompleteScores =
    Boolean(subjectKeyB) &&
    raw.writing !== "" &&
    raw.reading !== "" &&
    raw.listening !== "" &&
    raw.oral !== "";

  let gradeB = null;
  let neededTextB = null;
  let pointsAboveBoundaryB = null;

  if (hasCompleteScores && entryLangB) {
    const SPECS = [
      [raw.writing, 30],
      [raw.reading, 40],
      [raw.listening, 25],
      [raw.oral, 30],
    ];
    const total100 = SPECS.reduce(
      (sum, [score, max]) => sum + (Number(score) / max) * 100 * 0.25,
      0
    );
    const cuts = Array.from({ length: 7 }, (_, i) =>
      parseFloat(entryLangB[String(i + 1)])
    );
    for (let i = 6; i >= 0; i--) {
      if (total100 >= cuts[i]) {
        gradeB = i + 1;
        if (i < 6) {
          neededTextB = `Need ${(cuts[i + 1] - total100).toFixed(1)} percentage points for grade ${i + 2}`;
        }
        break;
      }
    }

    if (gradeB !== null) {
      pointsAboveBoundaryB = getPointsAboveBoundary(total100, entryLangB, gradeB);
    }
  }

  useEffect(() => {
    if (isLoading) return;

    if (track === "langB") {
      updateGrade(
        groupId,
        gradeB,
        subject ? subject.replace(`${level} `, "") : null,
        level,
        neededTextB
      );
    }
  }, [isLoading, gradeB, subject, level, neededTextB, track, updateGrade, groupId]);

  const fallbackBoundaryNote =
    resolvedBoundary && resolvedBoundary !== boundary
      ? `Result based on boundary "${resolvedBoundary}" because "${boundary}" is unavailable for this subject.`
      : null;


  /* ── track toggle (reused in both render paths) ── */
  const TrackToggle = () => (
    <FlowChoice label="Course type"><select value={track} onChange={e => switchTrack(e.target.value)}><option value="langB">Language B / ab initio</option><option value="langA">Language A</option></select></FlowChoice>
  );

  /* ── langA delegates to GroupOne ── */
  if (track === "langA") {
    return (
      <GroupOne
        groupId={groupId}
        boundary={boundary}
        onBoundaryNotFound={onBoundaryNotFound}
        headerExtras={headerExtras}
        afterTitleExtras={<>{afterTitleExtras}<TrackToggle /></>}
      />
    );
  }

  if (isLoading) {
    return <div className="calculator-pad"><p>Loading…</p></div>;
  }
  if (Object.keys(dataByBoundary).length === 0) {
    return <div className="calculator-pad"><p>Boundary data unavailable.</p></div>;
  }

  return (
    <FlowSubjectRow groupId={groupId} fields={MARK_FIELDS} scores={raw} onScoresChange={setRaw} grade={gradeB} neededText={neededTextB} pointsAboveBoundary={pointsAboveBoundaryB} fallbackBoundaryNote={fallbackBoundaryNote} error={boundaryNotFound ? "Boundary not available. Try another boundary (e.g., m24)." : null} levelControl={<FlowLevelSelect groupId={groupId} value={level} options={LEVELS} onChange={value => { setLvl(value); setSubject(subject ? subject.replace(/^(SL|HL) /, value + " ") : value + " Spanish B"); }} />} choices={<>
      {headerExtras}{afterTitleExtras}<TrackToggle />
      <FlowChoice label="Language"><select value={subject} disabled={!langOptions.length} onChange={e => setSubject(e.target.value)}><option value="" disabled>Language</option>{langOptions.map(value => <option key={value} value={value}>{value.replace(/^(SL|HL) /, "")}</option>)}</select></FlowChoice>
    </>} />
  );
}

export default React.memo(GroupTwo);
